/**
 * NORMALIZAÇÃO DE UNIDADES (Bloco 04, complemento).
 *
 * A planilha/PDF do cliente traz a unidade que ele usa. As validações
 * técnicas só podem comparar grandezas na MESMA unidade, então a
 * normalização acontece ANTES de qualquer validação.
 *
 * Duas garantias:
 * - O valor e a unidade ORIGINAIS nunca se perdem: ficam nos metadados do
 *   registro, para auditoria e para o usuário conferir o que veio do arquivo.
 * - Unidade desconhecida não é convertida nem descartada: o valor é
 *   preservado como veio e o campo entra como REVISÃO NECESSÁRIA.
 */

export type Dimension = "PRESSURE" | "TEMPERATURE" | "LENGTH";

/** Unidades canônicas do sistema, escolhidas para bater com o cadastro existente. */
export const CANONICAL_UNIT: Record<Dimension, string> = {
  PRESSURE: "kgf/cm²",
  TEMPERATURE: "°C",
  LENGTH: "mm"
};

export interface NormalizedMeasurement {
  /** Valor já convertido para a unidade canônica da grandeza. */
  readonly value: number;
  readonly unit: string;
  /** O que veio no arquivo, preservado. */
  readonly originalValue: number;
  readonly originalUnit: string;
  /** false quando a unidade não foi reconhecida — o valor fica como veio. */
  readonly recognized: boolean;
}

function normalizeUnitToken(unit: string): string {
  return unit
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[²⁲]/g, "2")
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9/°]/g, "")
    .toLowerCase();
}

/** Fatores para a unidade canônica. Temperatura não é fator, é fórmula. */
const PRESSURE_TO_KGFCM2: Record<string, number> = {
  "kgf/cm2": 1,
  "kgfcm2": 1,
  "kg/cm2": 1,
  "bar": 1.0197162129779,
  "barg": 1.0197162129779,
  "mpa": 10.197162129779,
  "kpa": 0.010197162129779,
  "psi": 0.070306957829636,
  "psig": 0.070306957829636
};

const LENGTH_TO_MM: Record<string, number> = {
  "mm": 1,
  "milimetro": 1,
  "milimetros": 1,
  "cm": 10,
  "m": 1000,
  "pol": 25.4,
  "in": 25.4,
  "\"": 25.4,
  "polegada": 25.4,
  "polegadas": 25.4
};

const TEMPERATURE_UNITS = new Set(["°c", "c", "celsius", "°f", "f", "fahrenheit", "k", "kelvin"]);

/** Converte para a unidade canônica da grandeza, preservando a origem. */
export function normalizeMeasurement(value: number, unit: string, dimension: Dimension): NormalizedMeasurement {
  const token = normalizeUnitToken(unit);
  const base = { originalValue: value, originalUnit: unit };

  // Unidade em branco: assume-se a canônica declarada pelo cabeçalho do
  // próprio arquivo, que já foi lida antes. Não é adivinhação de grandeza.
  if (token === "") {
    return { ...base, value, unit: CANONICAL_UNIT[dimension], recognized: true };
  }

  if (dimension === "PRESSURE") {
    const factor = PRESSURE_TO_KGFCM2[token];
    if (factor === undefined) return { ...base, value, unit, recognized: false };
    return { ...base, value: round(value * factor), unit: CANONICAL_UNIT.PRESSURE, recognized: true };
  }

  if (dimension === "LENGTH") {
    const factor = LENGTH_TO_MM[token];
    if (factor === undefined) return { ...base, value, unit, recognized: false };
    return { ...base, value: round(value * factor), unit: CANONICAL_UNIT.LENGTH, recognized: true };
  }

  if (!TEMPERATURE_UNITS.has(token)) return { ...base, value, unit, recognized: false };
  if (token === "°f" || token === "f" || token === "fahrenheit") {
    return { ...base, value: round((value - 32) * 5 / 9), unit: CANONICAL_UNIT.TEMPERATURE, recognized: true };
  }
  if (token === "k" || token === "kelvin") {
    return { ...base, value: round(value - 273.15), unit: CANONICAL_UNIT.TEMPERATURE, recognized: true };
  }
  return { ...base, value, unit: CANONICAL_UNIT.TEMPERATURE, recognized: true };
}

/** Arredonda a 6 casas para não propagar ruído de ponto flutuante na conversão. */
function round(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/** Grandeza de cada campo importado — usada para escolher a conversão certa. */
export const FIELD_DIMENSION: Record<string, Dimension> = {
  designPressure: "PRESSURE",
  operatingPressure: "PRESSURE",
  pmta: "PRESSURE",
  designTemperature: "TEMPERATURE",
  operatingTemperature: "TEMPERATURE",
  nominalThickness: "LENGTH",
  minimumThickness: "LENGTH"
};
